import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { agrupar, filtrar, itensDe } from './glossario';

/**
 * Glossário de siglas, em duas formas:
 *
 *  - completo: faixa azul com a etiqueta, o título, a busca e o índice das
 *    letras; embaixo, as siglas agrupadas por letra (sigla em destaque,
 *    significado e, quando houver, a que unidade ou diretoria pertence);
 *  - compacto: "Siglas desta página", uma lista recolhível para o fim da
 *    página.
 *
 * A ordem alfabética é automática. As âncoras das letras levam o id do bloco,
 * para dois glossários na mesma página não se cruzarem.
 */

const LETRAS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');

const ancora = (id, letra) =>
  `glossario-${id || 'bloco'}-${letra === '#' ? 'outros' : letra}`;

const Lista = ({ itens }) => (
  <dl className="ag-glossario__grade">
    {itens.map((x) => (
      <div className="ag-glossario__item" key={x.id}>
        <dt className="ag-glossario__sigla">{x.sigla}</dt>
        <dd className="ag-glossario__significado">
          {x.significado}
          {x.vinculo && (
            <span className="ag-glossario__vinculo">{x.vinculo}</span>
          )}
        </dd>
      </div>
    ))}
  </dl>
);

Lista.propTypes = { itens: PropTypes.array.isRequired };

const GlossarioView = ({ data = {}, id, vazio }) => {
  const [busca, setBusca] = useState('');
  const itens = itensDe(data);
  if (!itens.length) return vazio || null;
  const titulo = (data.titulo || '').trim();
  const subtitulo = (data.subtitulo || '').trim();
  const contagem = data.contagem !== false;

  if (data.forma === 'compacto') {
    return (
      <details
        className="ag-glossario ag-glossario--compacto"
        open={!!data.aberto}
      >
        <summary>
          <span className="ag-glossario__resumo">
            {titulo || 'Siglas desta página'}
          </span>
          {contagem && (
            <span className="ag-glossario__conta">{itens.length}</span>
          )}
        </summary>
        {subtitulo && <p className="ag-glossario__sub">{subtitulo}</p>}
        <Lista itens={itens} />
      </details>
    );
  }

  const Nivel = data.nivel === 'h3' ? 'h3' : 'h2';
  const NivelLetra = Nivel === 'h3' ? 'h4' : 'h3';
  const etiqueta = (data.etiqueta || '').trim();
  const vistos = filtrar(itens, busca);
  const grupos = agrupar(vistos);
  const presentes = new Set(grupos.map((g) => g.letra));
  const buscando = !!busca.trim();

  return (
    <section className="ag-glossario ag-glossario--completo">
      <div className="ag-glossario__topo">
        {etiqueta && (
          <span className="ag-glossario__etiqueta">
            {etiqueta}
            {contagem ? ` · ${itens.length}` : ''}
          </span>
        )}
        {titulo && <Nivel className="ag-glossario__titulo">{titulo}</Nivel>}
        {subtitulo && <p className="ag-glossario__sub">{subtitulo}</p>}
        <div className="ag-glossario__busca" role="search">
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar sigla ou nome"
            aria-label="Buscar no glossário de siglas"
          />
        </div>
        <nav className="ag-glossario__letras" aria-label="Letras do glossário">
          {LETRAS.filter((l) => l !== '#' || presentes.has('#')).map((l) =>
            presentes.has(l) ? (
              <a key={l} href={`#${ancora(id, l)}`}>
                {l}
              </a>
            ) : (
              <span key={l} aria-hidden="true">
                {l}
              </span>
            ),
          )}
        </nav>
      </div>
      <div className="ag-glossario__corpo">
        <p className="ag-glossario__resultado" role="status">
          {buscando
            ? vistos.length
              ? `${vistos.length} de ${itens.length} siglas`
              : `Nenhuma sigla encontrada para “${busca.trim()}”.`
            : ''}
        </p>
        {grupos.map((g) => (
          <div className="ag-glossario__bloco-letra" key={g.letra}>
            <NivelLetra
              className="ag-glossario__letra"
              id={ancora(id, g.letra)}
            >
              {g.letra}
            </NivelLetra>
            <Lista itens={g.itens} />
          </div>
        ))}
      </div>
    </section>
  );
};

GlossarioView.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  vazio: PropTypes.node,
};

export default GlossarioView;
